import { defineComponent as _defineComponent } from 'vue'
import { queries } from "@/apollo";
import {
  User,
  StatementType,
  ExternalCompany,
  TtCompanyReport,
} from "@/CodegenTypes";
import type { LegacyTtUserReport } from "@/types/reportTypes";
import { first, sumBy, some } from "lodash";
import { formatDollar } from "@/helpers/formatDollar";
import AccountSelectModal from "@/components/modules/account/AccountSelectModal.vue";
import AccountSelectSearch from "@/components/modules/account/AccountSelectSearch.vue";
import TtPositionStatementsModal from "@/components/modules/report/TtPositionStatementsModal.vue";
import TtStrikeStatementsModal from "@/components/modules/report/TtStrikeStatementsModal.vue";
import TtOutsetStrikeStatementsModal from "@/components/modules/report/TtOutsetStrikeStatementsModal.vue";
import TtTicketsModal from "@/components/modules/report/TtTicketsModal.vue";
import UserBreadcrumb from "@/components/common/UserBreadcrumb.vue";
import { canSeeTtCompanyLevel } from "@/helpers/user/canSeeTtCompanyLevel";
import { FEATURE_TOGGLES } from "@/config";
import CompanyPlTable from "@/components/modules/report/CompanyPlTable.vue";
import { useRoute, useRouter } from "vue-router/composables";
import { computed, ref } from "vue";
import { useQuery } from "@vue/apollo-composable";
import { useTranslation } from "@/store/translation";


export default /*#__PURE__*/_defineComponent({
  __name: 'FullReportLegacy',
  setup(__props) {

const showStrikeModal = ref(false);
const showOutsetStrikeModal = ref(false);
const showPositionModal = ref(false);
const showTicketsModal = ref(false);
const selectedUserId = ref<null | User["userId"]>(null);
const includeDownlines = ref(true);
const selectedExternalCompanyId = ref<null | ExternalCompany["id"]>(null);
const selectedStatementType = ref("ticketIn");
const showAccountSelectionModal = ref(false);

const router = useRouter();
const route = useRoute();
const { $t } = useTranslation();

const routeUserId = computed<User["userId"]>(
  () => route.query.userId as string
);

const selectedStartReleaseDate = computed(() => {
  const query = route.query.startReleaseDate;
  return Array.isArray(query) ? first(query) : query;
});

const selectedEndReleaseDate = computed(() => {
  const query = route.query.endReleaseDate;
  return Array.isArray(query) ? first(query) : query;
});

const { result: meResult, loading: meLoading } = useQuery<{ me: User }>(
  queries.me,
  null,
  {
    fetchPolicy: "cache-first",
  }
);

const { result: ttFullReportResult, loading: fullReportLoading } = useQuery<{
  ttFullReport: LegacyTtUserReport;
}>(queries.ttFullReportLegacy, {
  releaseDateStart: selectedStartReleaseDate,
  releaseDateEnd: selectedEndReleaseDate,
  userId: routeUserId,
});

const ttCompanyReportQuery = FEATURE_TOGGLES.useV1Legacy
  ? queries.ttCompanyReportLegacy
  : queries.ttCompanyReport;

const { result: ttCompanyReportResult, loading: companyReportLoading } =
  useQuery<{
    ttCompanyReport: TtCompanyReport;
  }>(ttCompanyReportQuery, {
    releaseDateStart: selectedStartReleaseDate,
    releaseDateEnd: selectedEndReleaseDate,
  });

const loading = computed(
  () => meLoading.value || fullReportLoading.value || companyReportLoading.value
);

const me = computed(() => meResult.value?.me);
const ttFullReport = computed(() => ttFullReportResult.value?.ttFullReport);

// const ttFullReport = ref<TtUserReport>();
// watch(
//   () => ttFullReportResult.value?.ttFullReport,
//   (value) => {
//     if (value) {
//       ttFullReport.value = value;
//     }
//   }
// );
const ttCompanyReport = computed(
  () => ttCompanyReportResult.value?.ttCompanyReport
);

const seeCompanyLevel = computed(() => canSeeTtCompanyLevel(me.value));

const isNotAdminViewingAdmin = computed(
  () => routeUserId.value === "admin" && me.value?.userId !== "admin"
);

const selectedUserStatement = computed(() => {
  if (!ttFullReport.value) {
    return null;
  }
  return [
    ttFullReport.value.consolidated,
    ...(ttFullReport.value.downlines || []),
  ].find((s) => s.userId === selectedUserId.value);
});

const showCompany = computed(() => routeUserId.value === "admin");

const netProfit = computed(() => {
  if (!ttFullReport.value) {
    return 0;
  }
  const netProfit =
    ttFullReport.value.consolidated.netBal - downlinesSummary.value.netBal;
  if (Math.abs(netProfit) < 0.001) {
    return 0;
  }
  return netProfit;
});

const companyPlStatement = computed(
  () =>
    ttCompanyReportResult.value?.ttCompanyReport?.companyTicketsFinal.summary
);

const downlinesWithTickets = computed(() =>
  ttFullReport.value?.downlines.filter((dl) =>
    some(
      Object.values(dl).filter((val) => typeof val === "number" && val !== 0)
    )
  )
);

const downlinesSummary = computed(() => ({
  amount: sumBy(downlinesWithTickets.value, (dl) => dl.amount),
  rebate: sumBy(downlinesWithTickets.value, (dl) => dl.rebate),
  amountLessRebate: sumBy(
    downlinesWithTickets.value,
    (dl) => dl.amountLessRebate
  ),
  strikeAmount: sumBy(downlinesWithTickets.value, (dl) => dl.strikeAmount),
  net: sumBy(downlinesWithTickets.value, (dl) => dl.net),
  companyStake: sumBy(downlinesWithTickets.value, (dl) => dl.companyStake),
  position: sumBy(downlinesWithTickets.value, (dl) => dl.position),
  netBal: sumBy(downlinesWithTickets.value, (dl) => dl.netBal),
}));

function showPosition(userId: User["userId"]) {
  selectedUserId.value = userId;
  showPositionModal.value = true;
}
function showStrike(userId: User["userId"], statementType: StatementType) {
  selectedUserId.value = userId;
  selectedStatementType.value = statementType;
  showStrikeModal.value = true;
}
function showTickets(userId: User["userId"], includeDownlinesInput: boolean) {
  selectedUserId.value = userId;
  includeDownlines.value = includeDownlinesInput;
  showTicketsModal.value = true;
}
function showOutsetStrike(userId: ExternalCompany["userId"]) {
  selectedExternalCompanyId.value = userId;
  showOutsetStrikeModal.value = true;
}

function seeAdmin() {
  router.push({
    name: "toto full report",
    query: { ...route.query, userId: "admin" },
  });
}

return { __sfc: true,showStrikeModal, showOutsetStrikeModal, showPositionModal, showTicketsModal, selectedUserId, includeDownlines, selectedExternalCompanyId, selectedStatementType, showAccountSelectionModal, router, route, $t, routeUserId, selectedStartReleaseDate, selectedEndReleaseDate, meResult, meLoading, ttFullReportResult, fullReportLoading, ttCompanyReportQuery, ttCompanyReportResult, companyReportLoading, loading, me, ttFullReport, ttCompanyReport, seeCompanyLevel, isNotAdminViewingAdmin, selectedUserStatement, showCompany, netProfit, companyPlStatement, downlinesWithTickets, downlinesSummary, showPosition, showStrike, showTickets, showOutsetStrike, seeAdmin, StatementType, formatDollar, AccountSelectModal, AccountSelectSearch, TtPositionStatementsModal, TtStrikeStatementsModal, TtOutsetStrikeStatementsModal, TtTicketsModal, UserBreadcrumb, FEATURE_TOGGLES, CompanyPlTable }
}

})